import { ApplicationState, BankDetails, Eligibility, IAttachedDocument, ReviewDeclarations } from "../store";
import { StandardAppConfig } from "../Components";
import { sanitiseFileName } from "./sanitise-file-name";

export interface RegistrationForm {
    correlationId: string;
    userId?: string;
    myAccountId: string;
    productCode: string;
    sourceChannel: string;
    submissionData: {
        productData?: Eligibility;
        referenceDocuments: {
            documentType: string;
            uri: string;
            name: string;
        }[];
        bankDetails?: {
            bankAccountName: string;
            bankAccountNo: string;
            bankBsb: string;
        };
        attributes: {
            name: string;
            value: string;
        }[];
        businessDetails?: {
            // hasAbn is an enum field being either Yes or No with a reason such as Charity or Not for Profit ...
            hasAbn?: string;
            abn?: string;
            businessName?: string;
            entityType?: string;
        };
        declarations: {
            [key: string]: boolean;
        };
    };
}

export type DataSubmissionMapper = (data: ApplicationState, config: StandardAppConfig) => RegistrationForm;

function transformReferenceDocuments(documents: Array<IAttachedDocument>) {
    return documents.map(transformReferenceDocument);
}

function transformReferenceDocument(d: IAttachedDocument) {
    return {
        uri: d.id,
        name: d.name ? sanitiseFileName(d.name) : d.name,
        documentType: d.documentType ? d.documentType : "DOCUMENT",
    };
}

function transformBankDetails(bankDetails: BankDetails) {
    if (bankDetails?.bsb) {
        return {
            bankAccountName: bankDetails?.accountName,
            bankAccountNo: bankDetails?.accountNumber,
            bankBsb: bankDetails?.bsb,
        };
    } else {
        // for eligibility check we have not yet entered and,
        // it would fail validation if we included this block with empty strings
        return undefined;
    }
}

const transformDeclarations = (declarations: ReviewDeclarations) =>
    Object.fromEntries(declarations.map((declaration) => [declaration.value, declaration.isChecked]));

/**
 * This function maps the UI Form application state to a back end data model in the standard way.
 * An application might choose to mapp the ndata
 * @param data
 * @param config
 */
export const defaultGaasApplicationDataMapper: DataSubmissionMapper = (
    data: ApplicationState,
    config: StandardAppConfig
): RegistrationForm => {
    const result = {
        correlationId: data.transactionDetails.correlationId,
        myAccountId: data.userInfo.id,
        // ideally we should not set the userId here, but should allow for apigee to set the header based
        // on the credentials in the auth token. We want the user id determined from the credentials tokens
        // rather than self identifying in the content of the request/
        // TODO: remove this when the back end can work with just the apigee sent header
        userId: data.userInfo.email,
        productCode: config.productCode,
        sourceChannel: "DIGITAL",
        submissionData: {
            productData: {
                ...data.eligibility,
                //TODO: possibly remap addresses to back end model???
            },
            // map business if it exists, but we only submit the selection, not all data from ABR.
            // the back end should retrieve the data from the private ABR directly and not trust abr data from FORM
            businessDetails: data.eligibility?.business
                ? {
                      hasAbn: data.eligibility?.hasAbn,
                      abn: data.eligibility?.business?.abn,
                      businessName: data.eligibility?.business?.businessName,
                      entityType: data.eligibility?.business?.entityType,
                  }
                : undefined,
            attributes: [
                {
                    // This uiAppRevision is a generic metadata field for analysis
                    name: "uiAppVersion",
                    value: config.appRevision,
                },
            ],
            // NOTE: we do not submit customer details as they are saved directly during the profile data call.
            referenceDocuments: transformReferenceDocuments(data.supportingDocuments.documents || []),
            bankDetails: transformBankDetails(data?.bankDetails),
            declarations: transformDeclarations(data.review.declarations),
        },
    };

    // business has its own mapping (above) don't put it into productData
    delete result.submissionData.productData.business;
    delete result.submissionData.productData.hasAbn;

    // check product data for addresses and transform
    const productData: object = result.submissionData.productData;

    if (productData != null) {
        if (config.projectArchetype === 'personalDisaster') {
            if (data.eligibility.ownerOrRenter === 'renter') {
                delete result.submissionData.productData.ownerOccupierOrInvestor;
            } else {
                delete result.submissionData.productData.peopleLivingAtAddress;
            }
        }
        // for business disasters we don't necessarily ask impacted address source as it is assumed
        // we don't use the customer's residential address, so we set it to other by default when blank
        console.log("bus addr check ", config.projectArchetype, productData["impactedAddressSource"]);
        if (config.projectArchetype === "businessDisaster" && !productData["impactedAddressSource"]) {
            productData["impactedAddressSource"] = "other";
        }

        if (config.projectArchetype === "businessDisaster") {
            if (result.submissionData.productData.hasFullTimeEmployees === "Yes") {
                delete result.submissionData.productData.soleTrader50PercentIncome;
            } else if (result.submissionData.productData.hasFullTimeEmployees === "No") {
                delete result.submissionData.productData.employees;
            }
        }

        if (productData["impactedAddressSource"] === "residential") {
            delete result.submissionData.productData.impactedAddress;
        } else if (productData["impactedAddressSource"] === "other") {
            const address = productData["impactedAddress"];
            if (typeof address === "object" && address) {
                productData["impactedAddress"] = mapToAddress(address);
            }
        }
        // impactedAddressMode is only required for frontend validation
        delete result.submissionData.productData.impactedAddressMode;
    }

    return result;
};

function mapToAddress(addressObject: any): object {
    const result: any = {
        addressId: addressObject.nswPointId?.value ?? "MANUAL",
        locality: addressObject.suburb?.value,
        street: addressObject.streetName?.value,
        postalCode: addressObject.postcode?.value,
    };
    for (const [key, value] of Object.entries(addressObject)) {
        // these fields have the same name, so we will just copy them
        const fieldsToCopy = ["state", "country", "formattedAddress", "streetNumber", "streetType"];
        if (fieldsToCopy.includes(key) && typeof value === "object") {
            // We don't need to return the whole object, nor errorMessage
            // indexes are ok as we get the index from the existing object, not from user input we are just remapping
            // the existing object, and we use a closed list above
            // eslint-disable-next-line security/detect-object-injection
            result[key] = value?.value;
        }
    }
    return result;
}
